// src/pages/AnnualSalesRealisation.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function AnnualSalesRealisation() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // core inputs
  const [projectYears, setProjectYears] = useState([]);
  const [capacityUtil, setCapacityUtil] = useState([]);
  const [products, setProducts] = useState([]); // [{ product }]
  const [rmInputs, setRmInputs] = useState([]); // [{ workingDays, workingHoursPerDay, outputPerHour, products }]
  const [salesInputs, setSalesInputs] = useState([]); // [{ product, inventoryDays, avgSalePrice }]
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });

  // fetch on mount
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setProjectYears(data.projectYears || []);
        setCapacityUtil(data.capacityUtilizationPerYear || []);
        setProducts(data.manufacturedProducts || []);
        setRmInputs(data.rmConsumptionInputs || []);
        setSalesInputs(data.annualSalesRealisationInputs || []);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });
      })
      .catch(() => setError("Failed to load data."))
      .finally(() => setLoading(false));
  }, [projectId]);

  // parse mm-yyyy
  const prod = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // months active in a FY label "YYYY-YY"
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel.slice(0, 4), 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  // 1) installed cap per product (MT/year)
  const installedByProduct = useMemo(() => {
    const m = {};
    rmInputs.forEach((r) => {
      const d = parseFloat(r.workingDays) || 0;
      const h = parseFloat(r.workingHoursPerDay) || 0;
      const o = parseFloat(r.outputPerHour) || 0;
      const inst = (d * h * o) / 1000;
      m[r.products] = (m[r.products] || 0) + inst;
    });
    return m;
  }, [rmInputs]);

  // 2) production MT per product per FY
  const productionByProduct = useMemo(() => {
    const out = {};
    projectYears.forEach((fy, yi) => {
      const util = parseFloat(capacityUtil[yi]) || 0;
      const months = getMonthsActive(fy);
      products.forEach(({ product }) => {
        const inst = installedByProduct[product] || 0;
        const mt = inst * (util / 100) * (months / 12);
        out[product] = out[product] || [];
        out[product][yi] = mt;
      });
    });
    return out;
  }, [
    projectYears,
    capacityUtil,
    products,
    installedByProduct,
    getMonthsActive,
  ]);

  // 3) avg working days
  const avgWorkingDays = useMemo(() => {
    if (!rmInputs.length) return 0;
    return (
      rmInputs.reduce((sum, r) => sum + (parseFloat(r.workingDays) || 0), 0) /
      rmInputs.length
    );
  }, [rmInputs]);

  // 4) avg inventory days & price map
  const avgInventoryDays = useMemo(() => {
    if (!salesInputs.length) return 0;
    return (
      salesInputs.reduce(
        (sum, s) => sum + (parseFloat(s.inventoryDays) || 0),
        0
      ) / salesInputs.length
    );
  }, [salesInputs]);

  const avgPriceMap = useMemo(() => {
    const m = {};
    salesInputs.forEach(
      (s) => (m[s.product] = parseFloat(s.avgSalePrice) || 0)
    );
    return m;
  }, [salesInputs]);

  // 5) build per-product
  const allData = useMemo(() => {
    return products.map(({ product }) => {
      const yearly = productionByProduct[product] || projectYears.map(() => 0);
      const closing = yearly.map((mt) =>
        avgWorkingDays ? (mt / avgWorkingDays) * avgInventoryDays : 0
      );
      const opening = closing.map((_, i) => (i > 0 ? closing[i - 1] : 0));
      const netSelling = yearly.map((mt, i) => opening[i] + mt - closing[i]);
      const price = avgPriceMap[product] || 0;
      const annualSalesLacs = netSelling.map((ns) => (ns * price) / 100000);
      const avgSalesPerMonth = annualSalesLacs.map((v) => v / 12);
      return {
        product,
        yearly,
        opening,
        closing,
        netSelling,
        price,
        annualSalesLacs,
        avgSalesPerMonth,
      };
    });
  }, [
    products,
    productionByProduct,
    projectYears,
    avgWorkingDays,
    avgInventoryDays,
    avgPriceMap,
  ]);

  // 6) totals per FY
  const totalAnnualSales = useMemo(
    () =>
      projectYears.map((_, yi) =>
        allData.reduce((sum, d) => sum + (d.annualSalesLacs[yi] || 0), 0)
      ),
    [projectYears, allData]
  );

  useEffect(() => {
    if (!projectId) return;
    API.put(`/user/project-inputs/${projectId}`, {
      totalAnnualSales: totalAnnualSales,
    }).catch((err) => {
      console.error("Failed to update totalAnnualSales on server:", err);
    });
  }, [projectId, totalAnnualSales]);

  // save totalAnnualSales to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        "totalAnnualSales",
        JSON.stringify(totalAnnualSales)
      );
    } catch {
      // ignore
    }
  }, [totalAnnualSales]);

  // 7) first non-zero FY
  const firstNonZero = useMemo(
    () =>
      projectYears.findIndex((fy, yi) => {
        const tot = products.reduce(
          (s, { product }) => s + (productionByProduct[product]?.[yi] || 0),
          0
        );
        return tot > 0;
      }),
    [projectYears, products, productionByProduct]
  );
  const startIdx = firstNonZero < 0 ? 0 : firstNonZero;
  const visibleYears = projectYears.slice(startIdx);

  // 8) totalClosingStock
  const totalClosingStockByYear = useMemo(() => {
    return projectYears.reduce((acc, fy, yi) => {
      const sum = allData.reduce((s, d) => s + (d.closing[yi] || 0), 0);
      acc[fy] = parseFloat(sum.toFixed(2));
      return acc;
    }, {});
  }, [projectYears, allData]);

  const totalClosingStockArray = useMemo(
    () => projectYears.map((fy) => totalClosingStockByYear[fy] || 0),
    [projectYears, totalClosingStockByYear]
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        "totalClosingStock",
        JSON.stringify(totalClosingStockArray)
      );
    } catch {
      // ignore
    }
  }, [totalClosingStockArray]);

  useEffect(() => {
    if (!projectId) return;
    API.put(`/user/project-inputs/${projectId}`, {
      totalClosingStock: totalClosingStockArray,
    }).catch((err) => {
      console.error("Failed to update totalClosingStock on server:", err);
    });
  }, [projectId, totalClosingStockArray]);

  // formatter
  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center w-full">
        {/* loading spinner */}
      </div>
    );
  if (error)
    return <div className="text-red-600 text-center py-8">{error}</div>;

  return (
    <div className="bg-white space-y-8">
      {allData.map((d, idx) => {
        const yearly = d.yearly.slice(startIdx);
        const opening = d.opening.slice(startIdx);
        const closing = d.closing.slice(startIdx);
        const netSelling = d.netSelling.slice(startIdx);
        const annualSalesLacs = d.annualSalesLacs.slice(startIdx);
        const avgSalesPerMonth = d.avgSalesPerMonth.slice(startIdx);

        return (
          <section key={idx} className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              {d.product}
            </h2>
            <div className="overflow-x-auto mb-8 shadow-lg rounded-lg">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-100">
                  <tr className="h-8">
                    <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                      Details
                    </th>
                    {visibleYears.map((yr, yi) => (
                      <th
                        key={yi}
                        className="px-2 py-1 border border-gray-200 text-center font-medium text-sm"
                      >
                        {yr}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Capacity Util (%)
                    </td>
                    {capacityUtil.slice(startIdx).map((u, ui) => (
                      <td
                        key={ui}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(u?.value ?? u ?? 0)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Opening Stock
                    </td>
                    {opening.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Production (MT)
                    </td>
                    {yearly.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Closing Stock
                    </td>
                    {closing.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Net Selling Qty
                    </td>
                    {netSelling.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Avg Sale Price / MT (Rs.)
                    </td>
                    {visibleYears.map((_, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(d.price)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Annual Sales (lacs)
                    </td>
                    {annualSalesLacs.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200 text-sm">
                      Avg Sales per mth
                    </td>
                    {avgSalesPerMonth.map((v, i) => (
                      <td
                        key={i}
                        className="px-2 py-1 border border-gray-200 text-sm text-center"
                      >
                        {fmt(v)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      <section className="space-y-2">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">
          Totals per FY
        </h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                  Details
                </th>
                {visibleYears.map((yr, yi) => (
                  <th
                    key={yi}
                    className="px-2 py-1 border border-gray-200 text-center font-medium text-sm"
                  >
                    {yr}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-50 font-semibold">
                <td className="px-2 py-1 border border-gray-200 text-sm">
                  Total Annual Sales
                </td>
                {totalAnnualSales.slice(startIdx).map((v, i) => (
                  <td
                    key={i}
                    className="px-2 py-1 border border-gray-200 text-sm text-center"
                  >
                    {fmt(v)}
                  </td>
                ))}
              </tr>
              <tr className="bg-gray-50 font-semibold">
                <td className="px-2 py-1 border border-gray-200 text-sm">
                  Total Closing Stock
                </td>
                {visibleYears.map((_, i) => (
                  <td
                    key={i}
                    className="px-2 py-1 border border-gray-200 text-sm text-center"
                  >
                    {fmt(totalClosingStockArray[startIdx + i] || 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
